/**
 * Template Listing Selectors
 *
 * Memoized selectors for accessing flight listing template state.
 * Using createSelector ensures components only re-render when selected data changes.
 */
import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from '@/store/store';

// ============================================
// Base Selectors
// ============================================

const selectTemplateListingState = (state: RootState) => state.flights.templateListing;

// ============================================
// Listing Selectors (Memoized)
// ============================================

export const selectIsReviewLoading = createSelector(
  [selectTemplateListingState],
  (listing) => listing.isReviewLoading,
);

// ============================================
// Preferences Selectors (Memoized)
// ============================================

/**
 * Get the preferences state
 */
export const selectPreferencesState = createSelector(
  [selectTemplateListingState],
  (listing) => listing.preferences,
);

/**
 * Get selected filters from preferences
 */
export const selectSelectedFilters = createSelector(
  [selectPreferencesState],
  (preferences) => preferences.selectedFilters,
);

/**
 * Get combined bitset from preferences
 * This is pre-computed when filters change (OR within category, AND across categories)
 */
export const selectCombinedBitSet = createSelector(
  [selectPreferencesState],
  (preferences) => preferences.combinedBitSet,
);

/**
 * Get the count of selected filters
 */
export const selectSelectedFiltersCount = createSelector(
  [selectSelectedFilters],
  (selectedFilters) => selectedFilters.length,
);

/**
 * Get the count of matching flights based on combined bitset
 */
export const selectMatchingFlightsCount = createSelector(
  [selectCombinedBitSet],
  (combinedBitSet) => {
    if (!combinedBitSet) return 0;
    return combinedBitSet.split('').filter((bit) => bit === '1').length;
  },
);

/**
 * Check if any filters are selected
 */
export const selectHasSelectedFilters = createSelector(
  [selectSelectedFiltersCount],
  (count) => count > 0,
);

// ============================================
// Autosuggest Selectors (Memoized)
// ============================================

/**
 * Get the autosuggest state
 */
export const selectAutosuggestState = createSelector(
  [selectTemplateListingState],
  (listing) => listing.autosuggest,
);

/**
 * Get autosuggest suggestions
 */
export const selectAutosuggestSuggestions = createSelector(
  [selectAutosuggestState],
  (autosuggest) => autosuggest.suggestions,
);

/**
 * Get autosuggest loading state
 */
export const selectIsAutosuggestLoading = createSelector(
  [selectAutosuggestState],
  (autosuggest) => autosuggest.isLoading,
);

/**
 * Get autosuggest error
 */
export const selectAutosuggestError = createSelector(
  [selectAutosuggestState],
  (autosuggest) => autosuggest.error,
);
